/**
 * PoolModelsAdmin — admin-only page for the Sun Stellar Pool Heat Pump
 * Calculator's model catalogue. The TDS values (capStd, capLow, etc.) are
 * read-only — they are physical product specs and only Sun Stellar
 * Engineering changes them (and bumps engine semver when they do).
 *
 * The ONLY field this page mutates is `in_stock`. Spec §11.1: in_stock is
 * admin-editable; spec §11.3: TDS values are stored in DB so future product
 * additions don't require code changes — but UI-side they're read-only.
 *
 * Track D — Sun Stellar (May 2026, Ananya).
 */
import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Check } from 'lucide-react';
import { api } from '@/utils/api';

interface ModelRow {
  id: string;
  cap_std_kw: number;
  cap_low_kw: number;
  pwr_std_kw: number;
  pwr_low_kw: number;
  cop_std: number;
  cop_low: number;
  flow_m3hr: number;
  in_stock: boolean;
}

export function PoolModelsAdmin() {
  const [rows, setRows] = useState<ModelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<ModelRow[]>('/pool-heat-pump/models')
      .then((r) => setRows(r.data))
      .catch((e) => setError(e?.response?.data?.error ?? 'Could not load models.'))
      .finally(() => setLoading(false));
  }, []);

  async function toggleStock(id: string, next: boolean) {
    setSavingId(id);
    setError(null);
    try {
      const res = await api.patch<ModelRow>(`/pool-heat-pump/models/${id}`, { in_stock: next });
      setRows((rs) => rs.map((r) => (r.id === id ? res.data : r)));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not update stock flag.');
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 20px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: '#003a6b' }}>
        Pool Heat Pump Models — admin
      </h1>
      <p style={{ color: '#666', marginTop: 4 }}>
        TDS values are read-only and shipped from Sun Stellar Engineering. The only
        editable field is the stock flag — flip it as inventory changes. The calculator
        picks "in stock" models by default; setting a model out-of-stock pushes it
        into the alternative-recommendation slot.
      </p>

      {error && (
        <div style={{ padding: 12, background: '#fde7e7', color: '#8b2222', borderRadius: 6, marginTop: 12, display: 'flex', gap: 8 }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <table style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f3f8ff', textAlign: 'left' }}>
              <th style={th}>Model</th>
              <th style={th}>cap (std / low) kW</th>
              <th style={th}>power (std / low) kW</th>
              <th style={th}>COP (std / low)</th>
              <th style={th}>Flow m³/h</th>
              <th style={th}>In stock?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={td}><strong>{r.id}</strong></td>
                <td style={td}>{r.cap_std_kw} / {r.cap_low_kw}</td>
                <td style={td}>{r.pwr_std_kw} / {r.pwr_low_kw}</td>
                <td style={td}>{r.cop_std} / {r.cop_low}</td>
                <td style={td}>{r.flow_m3hr}</td>
                <td style={td}>
                  <button
                    type="button"
                    onClick={() => toggleStock(r.id, !r.in_stock)}
                    disabled={savingId === r.id}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      border: 0,
                      cursor: savingId === r.id ? 'wait' : 'pointer',
                      background: r.in_stock ? '#d8f5d8' : '#fde7d8',
                      color: r.in_stock ? '#065c0c' : '#8a3a08',
                      display: 'inline-flex',
                      gap: 4,
                      alignItems: 'center',
                    }}
                  >
                    {savingId === r.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : r.in_stock ? (
                      <Check size={12} />
                    ) : null}
                    {r.in_stock ? 'IN STOCK' : 'MADE TO ORDER'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: '8px 12px', borderBottom: '2px solid #ddd', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 };
const td: React.CSSProperties = { padding: '10px 12px' };
